import { memo, useCallback, useEffect, useMemo, useRef, useState } from "react";
import { addDays, addMonths, endOfMonth, endOfWeek, format, startOfMonth, startOfWeek } from "date-fns";
import { de, enUS } from "date-fns/locale";
import axios from "axios";
import { useTranslation } from "react-i18next";
// Dasselbe Feiertags-Icon wie im Schichtplan; im Design System gibt es dafuer
// (noch) keinen Export.
import CelebrationOutlinedIcon from "@mui/icons-material/CelebrationOutlined";

import { API_BASE_URL } from "../../config";
import useToast from "../../components/useToast";
import DownloadBuilder from "../../components/DownloadBuilder";
import AppButton from "../../design-system/components/AppButton";
import AppLoadingOverlay from "../../design-system/components/AppLoadingOverlay";
import AppCallout from "../../design-system/components/AppCallout";
import AppCard from "../../design-system/components/AppCard";
import AppCardBody from "../../design-system/components/AppCardBody";
import { AppStatusChip } from "../../design-system/components/AppChip";
import AppConfirmDialog from "../../design-system/components/AppConfirmDialog";
import AppDialog from "../../design-system/components/AppDialog";
import AppDialogBody from "../../design-system/components/AppDialogBody";
import AppDialogFooter from "../../design-system/components/AppDialogFooter";
import AppDialogHeader from "../../design-system/components/AppDialogHeader";
import AppDivider from "../../design-system/components/AppDivider";
import AppFieldStack from "../../design-system/components/AppFieldStack";
import AppInput from "../../design-system/components/AppInput";
import AppDayHeader from "../../design-system/components/AppDayHeader";
import AppPersonBadge from "../../design-system/components/AppPersonBadge";
import AvailabilityModeRadioGroup from "../../components/AvailabilityModeRadioGroup";
import WorkingDayDefaultsEditor, {
  buildWorkingDayPayload,
  hasInvalidWorkingDayTimes,
  hasWorkingDaysChanged,
  workingDaysFromApi,
} from "../../components/WorkingDayDefaultsEditor";
import AppText from "../../design-system/components/AppText";
import AppTextarea from "../../design-system/components/AppTextarea";
import AppToolbar from "../../design-system/components/AppToolbar";
import AppTooltip from "../../design-system/components/AppTooltip";
import AppWorkspace from "../../design-system/components/AppWorkspace";
import { hasAppValueChanged } from "../../design-system/utils/appValueEquality";
import { filterAvailabilityApprovals } from "./pendingApprovals";
import {
  IconBlock,
  IconAvailability,
  IconError,
  IconHoliday,
  IconImport,
  IconNote,
  IconRemove,
  IconSettings,
  IconSuccess,
  IconTime,
} from "../../design-system/icons";
import { cellMeta } from "./availabilityCells";
import AvailabilitySectionTitle from "./AvailabilitySectionTitle";
import AvailabilitySettingsDialog from "./AvailabilitySettingsDialog";
import {
  defaultDraft,
  defaultExportTimeframeForMonth,
  defaultSettingsDraft,
  draftFromDay,
  draftFromSettings,
  holidaySaveRange,
  settingsPayloadFromDraft,
} from "./availabilityDrafts";
import ApprovalRequestsTable, { isApprovalRowSelectable } from "../approval_flow/ApprovalRequestsTable";
import AvailabilityEditor from "./AvailabilityEditor";
import AvailabilityDateField from "./AvailabilityDateField";
import AppPeriodNavigator from "../../design-system/components/AppPeriodNavigator";
import {
  deleteAvailabilityDay,
  exportAvailabilityFile,
  fetchAvailabilityCalendar,
  fetchAvailabilityExportPreview,
  fetchAvailabilityOverview,
  putAvailabilityDay,
  putAvailabilityDefaults,
  putAvailabilitySettings,
} from "./availabilityApi";
import "../../design-system/styles/tokens.css";
import "../../design-system/styles/components.css";
import "./availability.css";
import "./availabilityLegacy.css";

const toDateKey = (date) => format(date, "yyyy-MM-dd");
const cellKey = (row, dateKey) => `${row.user_id}::${dateKey}`;
// Wartezeit nach dem letzten Speichern, bevor die Bestaetigung erscheint.
const SAVED_TOAST_DELAY_MS = 1000;

const rangeForView = (view, anchorDate) => {
  if (view === "week") {
    return {
      start: startOfWeek(anchorDate, { weekStartsOn: 1 }),
      end: endOfWeek(anchorDate, { weekStartsOn: 1 }),
    };
  }
  return { start: startOfMonth(anchorDate), end: endOfMonth(anchorDate) };
};

// Ein Schritt vor oder zurück – je nach Ansicht eine Woche oder ein Monat.
const stepPeriod = (view, date, direction) =>
  view === "week" ? addDays(date, direction * 7) : addMonths(date, direction);

const isAvailableDay = (day) => (
  day?.status === "availability"
  && !day?.is_pending
  && !day?.isHoliday
);

const defaultAvailabilityLabel = (row, t, weekdayLabels) => {
  if (row.always_available) return t("availability.defaults.always_available");
  const weekdays = row.default_weekdays || [];
  if (!weekdays.length) return t("availability.defaults.no_days");
  return t("availability.defaults.available_days", {
    days: weekdays.map((weekday) => weekdayLabels[weekday]).join(", "),
  });
};

const EmployeeCell = ({ row, onAvatarClick, t, weekdayLabels }) => {
  const defaultLabel = defaultAvailabilityLabel(row, t, weekdayLabels);
  const availableWeekdays = new Set(row.default_weekdays || []);
  return (
    <div className="availability__employee">
      <AppPersonBadge
        name={row.name}
        size="md"
        onClick={() => onAvatarClick(row)}
        ariaLabel={t("availability.user_settings.open", { name: row.name })}
      />
      <div className="availability__employee-copy">
        <AppText variant="label-2" truncate className="availability__employee-name">{row.name}</AppText>
        <AppTooltip content={defaultLabel} placement="right">
          <span className="availability__weekday-dots">
            {weekdayLabels.map((label, weekday) => (
              <span key={label} className={`availability__weekday-dot${availableWeekdays.has(weekday) ? " is-available" : ""}`} />
            ))}
          </span>
        </AppTooltip>
      </div>
    </div>
  );
};

const EMPTY_SELECTION = new Set();

/*
  Die Statusmarken der Monatsansicht haengen an keiner Zelle: Sie kennen weder
  Datum noch Person. Als fertige Elemente einmal gebaut, spart das in einem
  vollen Monat ueber tausend Element-Erzeugungen pro Rendern — und React kann
  identische Elemente beim erneuten Rendern ueberspringen.
  Der Schluessel ist die Icon-Komponente aus cellMeta, weil derselbe Farbton
  (holiday) je nach Ursache zwei verschiedene Marken traegt.
*/
const monthMark = (Icon) => (
  <span className="availability__month-status-icon" aria-hidden="true"><Icon /></span>
);
const MONTH_STATUS_MARK = new Map([
  [IconBlock, monthMark(IconBlock)],
  [IconHoliday, monthMark(IconHoliday)],
  // Der Strich der leeren Zelle ist gezeichnet statt als Icon-Baustein: Er ist
  // die mit Abstand haeufigste Marke eines Monats, und ein Strich bleibt ein
  // Strich — die Masse steht in availability.css.
  [IconRemove, (
    <span
      className="availability__month-status-icon availability__month-status-icon--empty"
      aria-hidden="true"
    />
  )],
  [IconTime, monthMark(IconTime)],
  // IconSuccess = verfuegbar: In der Monatsansicht bleibt die Zelle leer,
  // die Flaechenfarbe sagt bereits alles.
  [IconSuccess, null],
]);

const COMMENT_MARK = (
  <span className="availability__cell-icon availability__cell-icon--comment" aria-hidden="true"><IconNote /></span>
);

/*
  Zaehler fuer die Zeitfenster, die in der Monatszelle keinen Platz mehr haben.
  Wie die Statusmarken oben einmal gebaut und wiederverwendet — es gibt nur eine
  Handvoll verschiedener Zahlen, aber potenziell viele Zellen.
*/
const EXTRA_WINDOW_MARKS = new Map();
const extraWindowMark = (count) => {
  if (!EXTRA_WINDOW_MARKS.has(count)) {
    EXTRA_WINDOW_MARKS.set(count, (
      <span className="availability__cell-icon availability__cell-icon--extra" aria-hidden="true">
        {`+${count}`}
      </span>
    ));
  }
  return EXTRA_WINDOW_MARKS.get(count);
};

/*
  Eine Zeile der Matrix — bewusst memoisiert.

  In der Monatsansicht haengen an jeder Zeile bis zu 31 Zellen mit je einem
  Tooltip. Ohne memo rendert jede Mausbewegung beim Ziehen ueber die Zellen die
  komplette Matrix neu: bei 50 Mitarbeitenden ueber 1500 Tooltips pro Ereignis.
  Mit memo aendert sich nur die Zeile, in der gerade ausgewaehlt wird — alle
  anderen behalten ihre Props und rendern gar nicht.

  Voraussetzung dafuer sind stabile Props: "columns" und "handlers" sind
  memoisiert, und "selectedDateKeys" ist fuer Zeilen ohne Auswahl immer
  dieselbe leere Menge.
*/
const MatrixRow = memo(function MatrixRow({
  row,
  columns,
  view,
  selectedDateKeys,
  handlers,
  t,
  weekdayLabels,
}) {
  return (
    <div className="availability__matrix-row">
      <div className="availability__employee-cell">
        <EmployeeCell
          row={row}
          onAvatarClick={handlers.onAvatarClick}
          t={t}
          weekdayLabels={weekdayLabels}
        />
      </div>
      {columns.map((column) => {
        const { dateKey } = column;
        const day = row.per_day?.[dateKey];
        const meta = cellMeta(day, t);
        const isMonth = view === "month";
        const accessibleStatus = meta.label === "–"
          ? t("availability.no_value")
          : meta.label.replace(/\n/g, " · ");
        const detailFlags = [
          day?.is_pending ? t("availability.pending_approval") : "",
          day?.has_comment ? t("availability.comment_available") : "",
        ].filter(Boolean);
        const selected = selectedDateKeys.has(dateKey);
        const cellClasses = [
          "availability__cell-wrap",
          selected ? "is-selected" : "",
          column.toneClass,
        ].filter(Boolean).join(" ");
        const buttonClasses = [
          "availability__cell",
          `availability__cell--${meta.tone}`,
          selected ? "is-selected" : "",
          meta.striped ? "is-striped" : "",
          meta.dashed ? "is-dashed" : "",
          isMonth && meta.extraWindows > 0 ? "has-extra" : "",
          day?.is_pending ? "is-pending" : "",
          day?.has_comment ? "has-comment" : "",
        ].filter(Boolean).join(" ");

        return (
          <div key={dateKey} className={cellClasses}>
            <button
              type="button"
              className={buttonClasses}
              onPointerDown={(event) => handlers.onPointerDown(event, row, dateKey)}
              onPointerEnter={(event) => handlers.onPointerEnter(event, row, dateKey)}
              onPointerUp={handlers.onPointerUp}
              onClick={(event) => handlers.onClick(event, row, dateKey)}
              aria-label={`${row.name}, ${column.fullDateLabel}: ${[accessibleStatus, ...detailFlags].join(", ")}`}
              /* Monatsansicht: der Hinweis haengt am Titel-Attribut statt an
                 einem eigenen Tooltip-Baustein. Bei 31 Spalten mal Belegschaft
                 waeren das ueber tausend Tooltip-Instanzen — genau die haben
                 die Ansicht ausgebremst. Dieselbe Loesung nutzen die
                 Tagesueberschriften darueber. */
              title={isMonth ? [column.fullDateLabel, accessibleStatus, ...detailFlags].join("\n") : undefined}
            >
              {isMonth
                ? (meta.monthLabel || MONTH_STATUS_MARK.get(meta.icon) || null)
                : meta.label}
              {isMonth && meta.extraWindows > 0 ? extraWindowMark(meta.extraWindows) : null}
              {day?.has_comment ? (
                isMonth ? COMMENT_MARK : (
                  <AppTooltip content={t("availability.comment_available")}>
                    <span className="availability__cell-icon availability__cell-icon--comment"><IconNote /></span>
                  </AppTooltip>
                )
              ) : null}
              {day?.is_pending && !isMonth ? (
                <AppTooltip content={t("availability.pending_approval")}>
                  <span className="availability__cell-icon availability__cell-icon--pending"><IconTime /></span>
                </AppTooltip>
              ) : null}
            </button>
          </div>
        );
      })}
    </div>
  );
});

const Availability = () => {
  const { t, i18n } = useTranslation();
  const locale = i18n.resolvedLanguage?.startsWith("de") ? de : enUS;
  const weekdayLabels = t("availability.weekdays.short", { returnObjects: true });
  const token = localStorage.getItem("session_token");
  const { success, error } = useToast();
  // Der Monat ist der uebliche Planungshorizont – deshalb der Default.
  const [view, setView] = useState("month");
  const [anchorDate, setAnchorDate] = useState(new Date());
  const [rows, setRows] = useState([]);
  const [publicHolidays, setPublicHolidays] = useState([]);
  const [overviewLoading, setOverviewLoading] = useState(true);
  /*
    AppWorkspace blendet sein Overlay nur beim ersten Laden ein. Bei jedem
    weiteren Zeitraumwechsel bliebe das Raster mit leeren Daten stehen — jede
    Zelle faellt dann auf "kein Wert" zurueck und zeigt einen Strich. Deshalb
    hier ein eigener Ladezustand fuer alles nach dem ersten Mal.
  */
  const [hasLoadedOnce, setHasLoadedOnce] = useState(false);
  const [loadError, setLoadError] = useState(false);
  const [editorOpen, setEditorOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const [selectedCell, setSelectedCell] = useState(null);
  const [selectedCells, setSelectedCells] = useState([]);
  const [selectionAnchor, setSelectionAnchor] = useState(null);
  const [calendar, setCalendar] = useState(null);
  const [draft, setDraft] = useState(defaultDraft(""));
  const appliesAs = "exception";
  const [search, setSearch] = useState("");
  const [showAvailabilityTotals, setShowAvailabilityTotals] = useState(false);
  const [isAddingMore, setIsAddingMore] = useState(false);
  const [isDesktopEditor, setIsDesktopEditor] = useState(() =>
    typeof window !== "undefined" ? window.matchMedia("(min-width: 1200px)").matches : true
  );
  const [settingsOpen, setSettingsOpen] = useState(false);
  const [settingsDraft, setSettingsDraft] = useState(defaultSettingsDraft());
  const [initialSettingsDraft, setInitialSettingsDraft] = useState(defaultSettingsDraft());
  const [savingSettings, setSavingSettings] = useState(false);
  const [exportOpen, setExportOpen] = useState(false);
  const [exportPreview, setExportPreview] = useState(null);
  const [exportFilters, setExportFilters] = useState({ statuses: [] });
  const [exportTimeframe, setExportTimeframe] = useState(() => defaultExportTimeframeForMonth(format(new Date(), "yyyy-MM")));
  const [exportTabMode, setExportTabMode] = useState("none");
  const [exportResetKey, setExportResetKey] = useState(0);
  const [exportPreviewLoading, setExportPreviewLoading] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [pendingOpen, setPendingOpen] = useState(false);
  const [pendingRows, setPendingRows] = useState([]);
  const [pendingLoading, setPendingLoading] = useState(false);
  const [pendingDecisionId, setPendingDecisionId] = useState("");
  const [pendingRejectRow, setPendingRejectRow] = useState(null);
  const [pendingApproveSelectedOpen, setPendingApproveSelectedOpen] = useState(false);
  const [approvingSelected, setApprovingSelected] = useState(false);
  const [selectedPendingIds, setSelectedPendingIds] = useState([]);
  const [pendingRejectComment, setPendingRejectComment] = useState("");
  const [selectedDefaultsUser, setSelectedDefaultsUser] = useState(null);
  const [userDefaultsDraft, setUserDefaultsDraft] = useState({
    availability_mode: "absence",
    working_days: [],
  });
  const [initialUserDefaultsDraft, setInitialUserDefaultsDraft] = useState({
    availability_mode: "absence",
    working_days: [],
  });
  const [userDefaultsSaving, setUserDefaultsSaving] = useState(false);
  const dragSelectionRef = useRef({ active: false, moved: false, rowId: null, startDate: "", suppressClick: false });
  const modifierOpenTimerRef = useRef(null);
  const autoSaveTimerRef = useRef(null);
  const autoSaveReadyRef = useRef(false);
  const savedToastTimerRef = useRef(null);
  const lastSavedSignatureRef = useRef("");
  const saveEntryRef = useRef(null);
  const editorRequestIdRef = useRef(0);

  const openUserDefaults = (row) => {
    setSelectedDefaultsUser(row);
    // default_availability traegt die gespeicherten Zeilen samt Zeiten. Ist noch
    // nichts konfiguriert, liefert nur default_weekdays die wirksamen Tage
    // (Rueckfallebene auf die Oeffnungstage) — dann ganztags vorbelegen.
    //
    // Was der Editor zeigt, ist damit immer der Stand, der auch gilt. Gespeichert
    // wird er nur, wenn er sich geaendert hat (siehe saveUserDefaults): eine
    // unberuehrte Vorbelegung bleibt Rueckfallebene statt zu festen Arbeitstagen
    // zu werden, die spaeter den Modus schlagen wuerden.
    const storedDays = workingDaysFromApi(row.default_availability);
    const nextDraft = {
      availability_mode: row.availability_mode === "availability" ? "availability" : "absence",
      working_days: storedDays.length
        ? storedDays
        : (row.default_weekdays || []).map((weekday) => ({
            weekday,
            isFullDay: true,
            startTime: "",
            endTime: "",
          })),
    };
    setUserDefaultsDraft(nextDraft);
    setInitialUserDefaultsDraft(nextDraft);
  };

  const closeUserDefaults = () => {
    if (userDefaultsSaving) return;
    setSelectedDefaultsUser(null);
  };

  const saveUserDefaults = async () => {
    if (
      !selectedDefaultsUser
      || !hasAppValueChanged(initialUserDefaultsDraft, userDefaultsDraft)
      || hasInvalidWorkingDayTimes(userDefaultsDraft.working_days)
    ) return;
    setUserDefaultsSaving(true);
    try {
      await putAvailabilitySettings(token, { compact: "editor" }, {
        user_id: selectedDefaultsUser.user_id,
        user: { availability_mode: userDefaultsDraft.availability_mode },
      });
      // Nur schreiben, was auch gemeint ist: eine unberuehrte Vorbelegung bleibt
      // Rueckfallebene und wird nicht zu gespeicherten Arbeitstagen.
      if (hasWorkingDaysChanged(initialUserDefaultsDraft, userDefaultsDraft)) {
        await putAvailabilityDefaults(token, {
          user_id: selectedDefaultsUser.user_id,
          working_day_defaults: buildWorkingDayPayload(userDefaultsDraft.working_days),
        }, { compact: "editor" });
      }
      await refreshOverviewUsers([selectedDefaultsUser.user_id]);
      success(t("availability.messages.user_settings_saved"));
      setSelectedDefaultsUser(null);
    } catch (requestError) {
      error(requestError?.response?.data?.message || t("availability.messages.user_settings_save_failed"));
    } finally {
      setUserDefaultsSaving(false);
    }
  };

  const range = useMemo(() => rangeForView(view, anchorDate), [view, anchorDate]);
  const dateKeys = useMemo(() => {
    const keys = [];
    for (let date = range.start; date <= range.end; date = addDays(date, 1)) keys.push(toDateKey(date));
    return keys;
  }, [range]);
  const filteredRows = useMemo(() => rows.filter((row) => {
    if (search && !row.name.toLowerCase().includes(search.toLowerCase())) return false;
    return true;
  }), [rows, search]);
  const availabilityTotals = useMemo(() => Object.fromEntries(
    dateKeys.map((dateKey) => [
      dateKey,
      rows.reduce(
        (count, row) => count + (isAvailableDay(row.per_day?.[dateKey]) ? 1 : 0),
        0,
      ),
    ]),
  ), [dateKeys, rows]);
  const publicHolidayNamesByDate = useMemo(
    () => Object.fromEntries(
      (publicHolidays || [])
        .filter((entry) => entry?.date)
        .map((entry) => [entry.date, (entry.names || []).join(", ")])
    ),
    [publicHolidays]
  );
  const isWeekendDate = useCallback((dateKey) => {
    const weekday = new Date(`${dateKey}T00:00:00`).getDay();
    return weekday === 0 || weekday === 6;
  }, []);
  // Wochenenden und Feiertage faerben die ganze Spalte – wie im Schichtplan.
  /*
    Alles, was nur vom Datum abhaengt, genau einmal pro Spalte statt einmal pro
    Zelle: Datum parsen und formatieren ist mit Locale teuer, und in der
    Monatsansicht faellt es sonst 31 × (Anzahl Mitarbeitende) Mal pro Rendern an.
  */
  const columns = useMemo(() => {
    const todayKey = toDateKey(new Date());
    return dateKeys.map((dateKey) => {
      const date = new Date(`${dateKey}T00:00:00`);
      const holidayName = publicHolidayNamesByDate[dateKey] || "";
      const weekend = isWeekendDate(dateKey);
      return {
        dateKey,
        date,
        holidayName,
        weekend,
        isToday: todayKey === dateKey,
        toneClass: [weekend ? "is-weekend" : "", holidayName ? "is-holiday" : ""]
          .filter(Boolean)
          .join(" "),
        weekdayLabel: format(date, view === "week" ? "EEEE" : "EEEEEE", { locale }),
        dayLabel: format(date, view === "week" ? "dd.MM." : "d"),
        fullDateLabel: format(date, "EEEE, dd. MMMM yyyy", { locale }),
        totalsDateLabel: format(date, "dd. MMMM yyyy", { locale }),
      };
    });
  }, [dateKeys, isWeekendDate, locale, publicHolidayNamesByDate, view]);

  /*
    Auswahl je Zeile. Zeilen ohne Auswahl bekommen immer dieselbe leere Menge —
    nur so bleibt ihre Prop referenzgleich und memo greift beim Ziehen.
  */
  const selectedKeysByRow = useMemo(() => {
    const byRow = new Map();
    selectedCells.forEach((cell) => {
      const rowId = cell.row?.user_id;
      if (rowId == null) return;
      const keys = byRow.get(rowId) || new Set();
      keys.add(cell.dateKey);
      byRow.set(rowId, keys);
    });
    return byRow;
  }, [selectedCells]);
  const datesBetween = useCallback((startDate, endDate) => {
    const startIndex = dateKeys.indexOf(startDate);
    const endIndex = dateKeys.indexOf(endDate);
    if (startIndex < 0 || endIndex < 0) return [];
    const from = Math.min(startIndex, endIndex);
    const to = Math.max(startIndex, endIndex);
    return dateKeys.slice(from, to + 1);
  }, [dateKeys]);

  const loadOverview = useCallback(async () => {
    if (!token) {
      setOverviewLoading(false);
      return;
    }
    setOverviewLoading(true);
    setLoadError(false);
    try {
      const response = await fetchAvailabilityOverview(token, {
        start_date: toDateKey(range.start),
        end_date: toDateKey(range.end),
      });
      setRows(response.data?.rows || []);
      setPublicHolidays(response.data?.public_holidays || []);
    } catch (requestError) {
      setLoadError(true);
    } finally {
      setOverviewLoading(false);
      setHasLoadedOnce(true);
    }
  }, [range, token]);

  const refreshOverviewUsers = useCallback(async (userIds) => {
    if (!token) return;
    const normalizedUserIds = [...new Set(userIds)].filter((userId) => userId != null);
    if (!normalizedUserIds.length) return;
    const response = await fetchAvailabilityOverview(token, {
      start_date: toDateKey(range.start),
      end_date: toDateKey(range.end),
      user_ids: normalizedUserIds.join(","),
    });
    const refreshedRows = new Map(
      (response.data?.rows || []).map((row) => [row.user_id, row])
    );
    setRows((currentRows) => currentRows.map((row) => refreshedRows.get(row.user_id) || row));
  }, [range, token]);

  /*
    Die offenen Anfragen haengen bewusst nicht am angezeigten Monat: Wer im
    Dezember steht, soll trotzdem sehen, dass im Maerz noch etwas offen ist.
    Deshalb einmal pro Sitzung laden statt erst beim Oeffnen des Dialogs — nur
    so zeigt der Button dieselbe Zahl, die der Dialog dann auch auflistet.
  */
  const loadPendingApprovals = useCallback(async ({ showLoading = true, notifyOnError = false } = {}) => {
    if (!token) return;
    // Beim stillen Nachladen bleibt die bereits sichtbare Liste stehen,
    // sonst blinkt der offene Dialog nach jedem Entscheid kurz auf "Laden...".
    if (showLoading) setPendingLoading(true);
    try {
      const response = await axios.get(`${API_BASE_URL}/api/web/approval`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setPendingRows(filterAvailabilityApprovals(response.data?.rows));
    } catch (requestError) {
      if (notifyOnError) {
        error(requestError?.response?.data?.message || t("availability.messages.pending_load_failed"));
      }
    } finally {
      if (showLoading) setPendingLoading(false);
    }
  }, [token, error, t]);

  useEffect(() => {
    loadOverview();
  }, [loadOverview]);

  useEffect(() => {
    loadPendingApprovals();
  }, [loadPendingApprovals]);

  /*
    Wird woanders entschieden (Sidebar-Badge, /approval/flow in einem zweiten
    Tab), soll die Zahl hier nicht stehen bleiben.
  */
  useEffect(() => {
    const refresh = () => loadPendingApprovals({ showLoading: false });
    window.addEventListener("approvals:changed", refresh);
    return () => window.removeEventListener("approvals:changed", refresh);
  }, [loadPendingApprovals]);

  useEffect(() => {
    setSelectedCells([]);
    setSelectionAnchor(null);
  }, [range]);

  useEffect(() => {
    const stopSelecting = () => {
      dragSelectionRef.current.active = false;
    };
    window.addEventListener("pointerup", stopSelecting);
    return () => window.removeEventListener("pointerup", stopSelecting);
  }, []);

  useEffect(() => () => {
    window.clearTimeout(modifierOpenTimerRef.current);
    window.clearTimeout(autoSaveTimerRef.current);
  }, []);

  useEffect(() => {
    const mediaQuery = window.matchMedia("(min-width: 1200px)");
    const updateEditorMode = (event) => setIsDesktopEditor(event.matches);
    setIsDesktopEditor(mediaQuery.matches);
    mediaQuery.addEventListener("change", updateEditorMode);
    return () => mediaQuery.removeEventListener("change", updateEditorMode);
  }, []);

  useEffect(() => {
    document.body.classList.toggle("availability-legacy-settings-open", settingsOpen);
    document.body.classList.toggle("availability-legacy-export-open", exportOpen);
    return () => {
      document.body.classList.remove("availability-legacy-settings-open");
      document.body.classList.remove("availability-legacy-export-open");
    };
  }, [exportOpen, settingsOpen]);

  const handleCellPointerDown = (event, row, dateKey) => {
    if (event.button !== 0 || event.shiftKey || event.metaKey || event.ctrlKey) return;
    window.clearTimeout(modifierOpenTimerRef.current);
    dragSelectionRef.current = { active: true, moved: false, row, rowId: row.user_id, startDate: dateKey, endDate: dateKey, suppressClick: false };
    setSelectedCells([{ row, dateKey }]);
    setSelectionAnchor({ row, dateKey });
  };

  const handleCellPointerEnter = (event, row, dateKey) => {
    const drag = dragSelectionRef.current;
    if (!drag.active || event.buttons !== 1 || drag.rowId !== row.user_id) return;
    const nextDates = datesBetween(drag.startDate, dateKey);
    dragSelectionRef.current = { ...drag, endDate: dateKey, moved: nextDates.length > 1, suppressClick: nextDates.length > 1 };
    setSelectedCells(nextDates.map((selectedDate) => ({ row, dateKey: selectedDate })));
  };

  const handleCellPointerUp = () => {
    const drag = dragSelectionRef.current;
    dragSelectionRef.current.active = false;
    if (drag.moved) {
      openEditor(datesBetween(drag.startDate, drag.endDate).map((dateKey) => ({ row: drag.row, dateKey })));
    }
  };

  /*
    Die Zellen-Handler greifen auf viel Zustand zu und waeren als useCallback
    staendig neu. Die Zeile bekommt deshalb ein Buendel, das sich nie aendert
    und intern immer die aktuelle Fassung aufruft — sonst faellt memo aus.
  */
  const cellHandlerImplRef = useRef(null);
  const cellHandlers = useMemo(() => ({
    onPointerDown: (event, row, dateKey) => cellHandlerImplRef.current.pointerDown(event, row, dateKey),
    onPointerEnter: (event, row, dateKey) => cellHandlerImplRef.current.pointerEnter(event, row, dateKey),
    onPointerUp: () => cellHandlerImplRef.current.pointerUp(),
    onClick: (event, row, dateKey) => cellHandlerImplRef.current.click(event, row, dateKey),
    onAvatarClick: (row) => cellHandlerImplRef.current.avatarClick(row),
  }), []);

  const handleCellClick = (event, row, dateKey) => {
    const drag = dragSelectionRef.current;
    if (drag.suppressClick) {
      dragSelectionRef.current = { ...drag, suppressClick: false };
      return;
    }
    if (event.shiftKey && selectionAnchor?.row?.user_id === row.user_id) {
      const nextCells = datesBetween(selectionAnchor.dateKey, dateKey).map((selectedDate) => ({ row, dateKey: selectedDate }));
      setSelectedCells(nextCells);
      openEditor(nextCells);
      return;
    }
    if (event.metaKey || event.ctrlKey) {
      setSelectionAnchor({ row, dateKey });
      setSelectedCells((current) => {
        const key = cellKey(row, dateKey);
        const nextCells = current.some((cell) => cellKey(cell.row, cell.dateKey) === key)
          ? current.filter((cell) => cellKey(cell.row, cell.dateKey) !== key)
          : [...current, { row, dateKey }];
        window.clearTimeout(modifierOpenTimerRef.current);
        if (nextCells.length) {
          modifierOpenTimerRef.current = window.setTimeout(() => openEditor(nextCells), 350);
        }
        return nextCells;
      });
      return;
    }
    if (isAddingMore) {
      const key = cellKey(row, dateKey);
      const nextCells = selectedCells.some((cell) => cellKey(cell.row, cell.dateKey) === key)
        ? selectedCells
        : [...selectedCells, { row, dateKey }];
      setSelectedCells(nextCells);
      setSelectionAnchor({ row, dateKey });
      setIsAddingMore(false);
      openEditor(nextCells);
      return;
    }
    const nextCells = [{ row, dateKey }];
    setSelectedCells(nextCells);
    setSelectionAnchor({ row, dateKey });
    openEditor(nextCells);
  };

  const openEditor = async (cellsOverride = selectedCells) => {
    const targets = [...cellsOverride].sort((left, right) => left.dateKey.localeCompare(right.dateKey));
    const firstTarget = targets[0];
    const row = firstTarget?.row;
    const dateKey = firstTarget?.dateKey;
    if (!row || !dateKey) return;
    const requestId = editorRequestIdRef.current + 1;
    editorRequestIdRef.current = requestId;
    autoSaveReadyRef.current = false;
    lastSavedSignatureRef.current = "";
    try {
      const response = await fetchAvailabilityCalendar(token, {
        month: dateKey.slice(0, 7),
        user_id: row.user_id,
        compact: "editor",
      });
      if (requestId !== editorRequestIdRef.current) return;
      const nextCalendar = response.data;
      const day = (nextCalendar?.days || []).find((item) => item.date === dateKey);
      setCalendar(nextCalendar);
      const nextDraft = draftFromDay(day, nextCalendar?.holiday_types || []);
      /*
        Klick auf einen Tag bestehender Ferien: deren echten Zeitraum behalten,
        statt ihn auf den angeklickten Tag zu stutzen — sonst zeigen die
        Von/Bis-Felder einen falschen Zeitraum und schon eine Aenderung am
        Ferientyp wuerde die uebrigen Ferientage entfernen. Bei einer
        Mehrfachauswahl zieht der Nutzer den Zeitraum bewusst selbst auf.
      */
      const keepsExistingHolidayRange = targets.length === 1
        && nextDraft.entry_kind === "holiday"
        && Boolean(nextDraft.start_date);
      const initializedDraft = {
        ...nextDraft,
        entry_kind: nextDraft.entry_kind === "free_request" ? "absence" : nextDraft.entry_kind,
        start_date: keepsExistingHolidayRange ? nextDraft.start_date : targets[0].dateKey,
        end_date: keepsExistingHolidayRange ? nextDraft.end_date : targets[targets.length - 1].dateKey,
      };
      setSelectedCell({ row, dateKey, targets });
      setDraft(initializedDraft);
      setEditorOpen(true);
      lastSavedSignatureRef.current = JSON.stringify({
        draft: initializedDraft,
        appliesAs,
        targets: targets.map((target) => cellKey(target.row, target.dateKey)).sort(),
      });
      window.setTimeout(() => { autoSaveReadyRef.current = true; }, 0);
    } catch (requestError) {
      if (requestId !== editorRequestIdRef.current) return;
      lastSavedSignatureRef.current = "";
      error(requestError?.response?.data?.message || t("availability.messages.entry_load_failed"));
    }
  };

  cellHandlerImplRef.current = {
    pointerDown: handleCellPointerDown,
    pointerEnter: handleCellPointerEnter,
    pointerUp: handleCellPointerUp,
    click: handleCellClick,
    avatarClick: openUserDefaults,
  };

  const closeEditor = () => {
    editorRequestIdRef.current += 1;
    setEditorOpen(false);
    setSelectedCell(null);
    setCalendar(null);
    setDraft(defaultDraft(""));
  };

  /*
    Der Editor speichert automatisch, waehrend getippt und geklickt wird. Jeder
    Speichervorgang schiebt die Bestaetigung nach hinten — am Ende einer Serie
    steht genau ein Toast, nicht einer pro Zwischenstand.
  */
  const announceSaved = useCallback(() => {
    window.clearTimeout(savedToastTimerRef.current);
    savedToastTimerRef.current = window.setTimeout(() => {
      success(t("availability.editor.saved"));
    }, SAVED_TOAST_DELAY_MS);
  }, [success, t]);

  useEffect(() => () => window.clearTimeout(savedToastTimerRef.current), []);

  const saveEntry = async () => {
    if (!selectedCell || !draft.entry_kind) return;
    const targetsByUser = new Map();
    selectedCell.targets.forEach((target) => {
      const existing = targetsByUser.get(target.row.user_id) || { row: target.row, dates: [] };
      existing.dates.push(target.dateKey);
      targetsByUser.set(target.row.user_id, existing);
    });
    setSaving(true);
    try {
      if (appliesAs === "standard" && ["availability", "absence"].includes(draft.entry_kind)) {
        for (const { row, dates } of targetsByUser.values()) {
          for (const dateKey of dates) {
            await deleteAvailabilityDay(
              token,
              { month: dateKey.slice(0, 7), user_id: row.user_id, compact: "editor" },
              { date: dateKey, user_id: row.user_id }
            );
          }
          await putAvailabilityDefaults(token, {
            user_id: row.user_id,
            weekdays: [...new Set(dates.map((dateKey) => new Date(`${dateKey}T00:00:00`).getDay()).map((day) => day === 0 ? 6 : day - 1))],
            ...draft,
          }, { compact: "editor" });
        }
      } else {
        const selectionDates = selectedCell.targets.map((target) => target.dateKey).sort();
        for (const { row, dates } of targetsByUser.values()) {
          const targetDates = [...dates].sort();
          const contiguous = targetDates.every((dateKey, index) => index === 0 || datesBetween(targetDates[index - 1], dateKey).length === 2);
          const holidayRange = draft.entry_kind === "holiday"
            ? holidaySaveRange({
              draftStartDate: draft.start_date,
              draftEndDate: draft.end_date,
              selectionDates,
              userDates: targetDates,
              contiguous,
            })
            : null;
          if (holidayRange) {
            await putAvailabilityDay(token, { month: holidayRange.start_date.slice(0, 7), user_id: row.user_id, compact: "editor" }, {
              ...draft,
              ...holidayRange,
              date: holidayRange.start_date,
              user_id: row.user_id,
            });
          } else {
            for (const dateKey of targetDates) {
              await putAvailabilityDay(token, { month: dateKey.slice(0, 7), user_id: row.user_id, compact: "editor" }, {
                ...draft,
                date: dateKey,
                user_id: row.user_id,
                start_date: draft.entry_kind === "holiday" ? dateKey : draft.start_date,
                end_date: draft.entry_kind === "holiday" ? dateKey : draft.end_date,
              });
            }
          }
        }
      }
      announceSaved();
      try {
        await refreshOverviewUsers([...targetsByUser.keys()]);
      } catch {
        error(t("availability.messages.entry_refresh_failed"));
      }
    } catch (requestError) {
      error(requestError?.response?.data?.message || t("availability.messages.entry_save_failed"));
    } finally {
      setSaving(false);
    }
  };
  saveEntryRef.current = saveEntry;

  useEffect(() => {
    if (!editorOpen || !autoSaveReadyRef.current || !draft.entry_kind || saving) return undefined;
    const signature = JSON.stringify({
      draft,
      appliesAs,
      targets: selectedCell?.targets.map((target) => cellKey(target.row, target.dateKey)).sort(),
    });
    if (signature === lastSavedSignatureRef.current) return undefined;
    window.clearTimeout(autoSaveTimerRef.current);
    autoSaveTimerRef.current = window.setTimeout(() => {
      lastSavedSignatureRef.current = signature;
      saveEntryRef.current?.();
    }, 550);
    return () => window.clearTimeout(autoSaveTimerRef.current);
  }, [draft, appliesAs, editorOpen, selectedCell, saving]);

  const selectedDayData = selectedCell
    ? (calendar?.days || []).find((day) => day.date === selectedCell.dateKey)
    : null;
  const selectedItems = (selectedCell?.targets || []).map((target) => ({
    id: cellKey(target.row, target.dateKey),
    label: `${target.row.name} · ${format(new Date(`${target.dateKey}T00:00:00`), "dd.MM.yyyy")}`,
    userId: target.row.user_id,
    userName: target.row.name,
    dateKey: target.dateKey,
    dateLabel: format(new Date(`${target.dateKey}T00:00:00`), "EEE, dd.MM", { locale }),
  }));
  const removeSelectedItem = (ids) => {
    const removedIds = new Set(Array.isArray(ids) ? ids : [ids]);
    const previousTargets = selectedCell?.targets || [];
    const nextTargets = previousTargets.filter((target) => !removedIds.has(cellKey(target.row, target.dateKey)));
    setSelectedCells(nextTargets);
    if (!nextTargets.length) {
      closeEditor();
      return;
    }
    setSelectedCell((current) => ({ ...current, targets: nextTargets, row: nextTargets[0].row, dateKey: nextTargets[0].dateKey }));
    // Der Ferien-Zeitraum folgt der Auswahl weiter — aber nur, solange er ihr
    // entspricht: ein von Hand aufgezogener Zeitraum bleibt stehen.
    const previousDates = previousTargets.map((target) => target.dateKey).sort();
    const nextDates = nextTargets.map((target) => target.dateKey).sort();
    setDraft((current) => (
      current.entry_kind === "holiday"
        && current.start_date === previousDates[0]
        && current.end_date === previousDates[previousDates.length - 1]
        ? { ...current, start_date: nextDates[0], end_date: nextDates[nextDates.length - 1] }
        : current
    ));
  };
  const clearSelectionAndClose = () => {
    setSelectedCells([]);
    setSelectionAnchor(null);
    setIsAddingMore(false);
    closeEditor();
  };

  const currentMonth = format(anchorDate, "yyyy-MM");
  const buildExportParams = (
    filters = exportFilters,
    timeframe = exportTimeframe,
    tabMode = exportTabMode,
  ) => {
    if (timeframe?.mode === "range") {
      if (!timeframe.start_date || !timeframe.end_date || timeframe.end_date < timeframe.start_date) return null;
      return {
        start_date: timeframe.start_date,
        end_date: timeframe.end_date,
        tab_mode: tabMode,
        ...(filters?.statuses?.length ? { statuses: filters.statuses.join(",") } : {}),
      };
    }
    return {
      month: timeframe?.month || currentMonth,
      tab_mode: tabMode,
      ...(filters?.statuses?.length ? { statuses: filters.statuses.join(",") } : {}),
    };
  };
  const loadExportPreview = async (
    filters = exportFilters,
    timeframe = exportTimeframe,
    tabMode = exportTabMode,
  ) => {
    const params = buildExportParams(filters, timeframe, tabMode);
    if (!params) return null;
    setExportPreviewLoading(true);
    try {
      const response = await fetchAvailabilityExportPreview(token, params);
      setExportPreview(response.data);
      return response.data;
    } catch (requestError) {
      error(requestError?.response?.data?.message || t("availability.messages.export_preview_failed"));
      return null;
    } finally {
      setExportPreviewLoading(false);
    }
  };
  const openExport = async () => {
    const filters = { statuses: [] };
    const timeframe = defaultExportTimeframeForMonth(currentMonth);
    setExportPreview(null);
    setExportFilters(filters);
    setExportTimeframe(timeframe);
    setExportTabMode("none");
    setExportOpen(true);
    if (!await loadExportPreview(filters, timeframe, "none")) return;
    setExportResetKey((current) => current + 1);
  };
  const handleExport = async ({
    format: exportFormat = "excel",
    columns,
    tab_mode: tabMode,
    filters,
    timeframe,
    settings,
  }) => {
    if (!buildExportParams(filters, timeframe)) {
      error(t("availability.messages.invalid_period"));
      return;
    }
    const isPdfExport = exportFormat === "pdf";
    const extension = isPdfExport ? "pdf" : "xlsx";
    const formatLabel = isPdfExport ? "PDF" : "Excel";
    setExporting(true);
    try {
      const response = await exportAvailabilityFile(token, {
        format: exportFormat,
        columns,
        tab_mode: tabMode,
        filters,
        timeframe,
        settings,
      });
      const fileBlob = response.data instanceof Blob
        ? response.data
        : new Blob(
          [response.data],
          {
            type: isPdfExport
              ? "application/pdf"
              : "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
          }
        );
      const url = window.URL.createObjectURL(fileBlob);
      const link = document.createElement("a");
      link.href = url;
      const previewFileName = exportPreview?.file_name?.replace(/\.xlsx$/i, `.${extension}`);
      link.setAttribute("download", previewFileName || `Verfügbarkeit_${currentMonth}.${extension}`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      setExportOpen(false);
      success(t("availability.messages.export_success", { format: formatLabel }));
    } catch (requestError) {
      error(requestError?.response?.data?.message || t("availability.messages.export_failed", { format: formatLabel }));
    } finally {
      setExporting(false);
    }
  };
  const openSettings = async () => {
    try {
      const response = await fetchAvailabilityCalendar(token, { month: currentMonth });
      const nextDraft = draftFromSettings(response.data?.settings);
      setSettingsDraft(nextDraft);
      setInitialSettingsDraft(nextDraft);
      setSettingsOpen(true);
    } catch (requestError) {
      error(requestError?.response?.data?.message || t("availability.messages.settings_load_failed"));
    }
  };
  const saveSettings = async () => {
    if (!hasAppValueChanged(initialSettingsDraft, settingsDraft)) return;
    if (
      settingsDraft.company.edit_lock_enabled
      && (settingsDraft.company.edit_lock_day_of_month === ""
        || settingsDraft.company.edit_lock_day_of_month == null
        || settingsDraft.company.edit_lock_horizon_months === ""
        || settingsDraft.company.edit_lock_horizon_months == null)
    ) {
      error(t("availability.messages.settings_validation"));
      return;
    }
    setSavingSettings(true);
    try {
      const response = await putAvailabilitySettings(
        token,
        { month: currentMonth },
        settingsPayloadFromDraft(settingsDraft)
      );
      setSettingsDraft(draftFromSettings(response.data?.settings));
      setSettingsOpen(false);
      success(t("availability.messages.settings_saved"));
      loadOverview();
    } catch (requestError) {
      error(requestError?.response?.data?.message || t("availability.messages.settings_save_failed"));
    } finally {
      setSavingSettings(false);
    }
  };
  const openPendingApprovals = () => {
    setPendingOpen(true);
    // Beim Oeffnen sind die Zeilen meist schon da; dann frisch nachladen,
    // ohne die Liste gegen einen Ladetext zu tauschen.
    loadPendingApprovals({ showLoading: pendingRows.length === 0, notifyOnError: true });
  };
  const decidePendingApproval = async (row, decision, decisionComment = "") => {
    setPendingDecisionId(row.id);
    try {
      const response = await axios.post(`${API_BASE_URL}/api/web/approval/decision`, {
        source: row.source,
        source_id: row.source_id,
        decision,
        decision_comment: decision === "rejected" ? decisionComment : "",
      }, { headers: { Authorization: `Bearer ${token}` } });
      setPendingRows(filterAvailabilityApprovals(response.data?.rows));
      if (decision === "rejected") {
        setPendingRejectRow(null);
        setPendingRejectComment("");
      }
      success(t(decision === "approved"
        ? "availability.messages.approval_approved"
        : "availability.messages.approval_rejected"));
      window.dispatchEvent(new Event("approvals:changed"));
      loadOverview();
    } catch (requestError) {
      error(requestError?.response?.data?.message || t("availability.messages.approval_failed"));
    } finally {
      setPendingDecisionId("");
    }
  };

  /*
    Die angehakten Anfragen — und nur solche, die gerade auch in der Liste
    stehen. Nach einem Nachladen verschwundene Zeilen fallen damit aus der
    Auswahl, statt beim naechsten Klick stillschweigend mitzulaufen.
  */
  const selectedPendingRows = useMemo(() => {
    const selectableById = new Map(
      pendingRows.filter(isApprovalRowSelectable).map((row) => [row.id, row])
    );
    return selectedPendingIds.map((id) => selectableById.get(id)).filter(Boolean);
  }, [pendingRows, selectedPendingIds]);

  const approveSelectedPending = async () => {
    if (!selectedPendingRows.length || approvingSelected) return;
    setApprovingSelected(true);
    try {
      const response = await axios.post(`${API_BASE_URL}/api/web/approval/decision/bulk`, {
        decision: "approved",
        items: selectedPendingRows.map((row) => ({ source: row.source, source_id: row.source_id })),
      }, { headers: { Authorization: `Bearer ${token}` } });
      setPendingRows(filterAvailabilityApprovals(response.data?.rows));
      setSelectedPendingIds([]);
      setPendingApproveSelectedOpen(false);
      const approvedCount = Number(response.data?.approved_count || 0);
      const skippedCount = Number(response.data?.skipped_count || 0);
      // Uebersprungen wird, was zwischenzeitlich jemand anderes entschieden hat
      // — das ist keine Fehlermeldung wert, aber verschweigen darf man es auch
      // nicht, sonst stimmt die Zahl im Dialog nicht mit der Rueckmeldung.
      success(skippedCount
        ? t("availability.messages.approval_bulk_partial", { count: approvedCount, skipped: skippedCount })
        : t("availability.messages.approval_bulk_approved", { count: approvedCount }));
      window.dispatchEvent(new Event("approvals:changed"));
      loadOverview();
    } catch (requestError) {
      error(requestError?.response?.data?.message || t("availability.messages.approval_failed"));
    } finally {
      setApprovingSelected(false);
    }
  };

  const settingsHasUnsavedChanges = hasAppValueChanged(initialSettingsDraft, settingsDraft);

  const editorPanelContent = selectedCell && editorOpen ? (
    <AvailabilityEditor
      dateKey={selectedCell.dateKey}
      mode={calendar?.selected_mode || "absence"}
      draft={draft}
      setDraft={setDraft}
      dayData={selectedDayData}
      holidayTypes={calendar?.holiday_types || []}
      onClose={clearSelectionAndClose}
      selectedItems={selectedItems}
      onRemoveSelected={removeSelectedItem}
      commentLabel={t("availability.editor.comment")}
    />
  ) : null;

  // Nachladen eines anderen Zeitraums: AppWorkspace zeigt dafuer nichts mehr,
  // also legt die Matrix ihr eigenes Overlay ueber den alten Stand.
  const isRefreshing = overviewLoading && hasLoadedOnce;

  return (
    <AppWorkspace
      title={t("availability.title")}
      className="availability"
      loading={overviewLoading}
    >
      {loadError ? (
        <AppCallout
          variant="error"
          title={t("availability.load_failed")}
          description={t("availability.load_failed_description")}
        />
      ) : null}

      <div className="availability__content-layout">
        <div className="availability__main-column">
          <AppToolbar
            className="availability__calendar-toolbar"
            left={(
              <>
                {/* Dieselbe Navigation wie auf /shiftplan und /demand. */}
                <AppPeriodNavigator
                  viewMode={view}
                  referenceDate={anchorDate}
                  previousLabel={t("availability.previous_period")}
                  nextLabel={t("availability.next_period")}
                  weekLabel={t("availability.week")}
                  monthLabel={t("availability.month")}
                  onPreviousPeriod={() => setAnchorDate((current) => stepPeriod(view, current, -1))}
                  onNextPeriod={() => setAnchorDate((current) => stepPeriod(view, current, 1))}
                  onSelectDate={setAnchorDate}
                  onViewModeChange={setView}
                />
                {/*
                  Ausgeschrieben statt nur die Zahl: Der Button ist sonst nicht
                  als Einstieg in die Genehmigungen zu erkennen. Die Zahl zaehlt
                  alle offenen Anfragen, nicht nur die des gezeigten Monats —
                  sie muss zum Inhalt des Dialogs passen.

                  Ohne offene Anfragen faellt der Button ganz weg. Ist die
                  Freigabe fuer Abwesenheiten abgeschaltet, entsteht gar nie
                  eine Anfrage — ein "Keine ausstehenden Anfragen" stuende dann
                  dauerhaft in der Leiste, ohne je etwas anderes zu sagen.
                */}
                {pendingRows.length ? (
                  <button
                    type="button"
                    className="availability__pending-chip-button"
                    onClick={openPendingApprovals}
                  >
                    <AppStatusChip status="warning" size="md" leadingIcon={<IconTime />}>
                      {t("availability.pending.count_label", { count: pendingRows.length })}
                    </AppStatusChip>
                  </button>
                ) : null}
              </>
            )}
            right={(
              <>
                <AppInput
                  type="search"
                  size="md"
                  clearable
                  placeholder={t("availability.search_employees")}
                  value={search}
                  onChange={(event) => setSearch(event.target.value)}
                  aria-label={t("availability.search_employees")}
                  className="availability__search"
                />
                <AppTooltip content={t("availability.export")}>
                  <AppButton variant="neutral" size="md" iconOnly onClick={openExport} aria-label={t("availability.export")}>
                    <IconImport />
                  </AppButton>
                </AppTooltip>
                <AppTooltip content={t("availability.settings")}>
                  <AppButton variant="neutral" size="md" iconOnly onClick={openSettings} aria-label={t("availability.settings")}>
                    <IconSettings />
                  </AppButton>
                </AppTooltip>
              </>
            )}
          />

          <AppCard className="availability__calendar-card">
          <AppCardBody inset="none">
            <div className="availability__matrix-pane">
              {isRefreshing ? <AppLoadingOverlay open scope="contained" /> : null}
              <div
                className={[
                  "availability__matrix-scroll",
                  view === "week" ? "is-week-view" : "is-month-view",
                  isRefreshing ? "is-refreshing" : "",
                ].filter(Boolean).join(" ")}
              >
                <div
                  className="availability__matrix"
                  style={{
                    "--availability-grid-columns": view === "week"
                      ? "215px repeat(7, minmax(0, 1fr))"
                      : `230px repeat(${dateKeys.length}, minmax(52px, 1fr))`,
                    "--availability-grid-min-width": view === "week" ? "0px" : "max-content",
                  }}
                >
                  <div className="availability__matrix-corner">
                    <span>{t("availability.employees")}</span>
                    <AppTooltip
                      content={t(showAvailabilityTotals
                        ? "availability.hide_available_totals"
                        : "availability.show_available_totals")}
                    >
                      <button
                        type="button"
                        className={`availability__totals-toggle${showAvailabilityTotals ? " is-on" : ""}`}
                        onClick={() => setShowAvailabilityTotals((current) => !current)}
                        aria-pressed={showAvailabilityTotals}
                      >
                        {showAvailabilityTotals ? "– Summe" : "+ Summe"}
                      </button>
                    </AppTooltip>
                  </div>
                  {columns.map((column) => (
                    <AppDayHeader
                      key={column.dateKey}
                      view={view}
                      className={`availability__date-header${column.toneClass ? ` ${column.toneClass}` : ""}`}
                      weekday={column.weekdayLabel}
                      day={column.dayLabel}
                      weekend={column.weekend}
                      holiday={Boolean(column.holidayName)}
                      today={column.isToday}
                      /* Feiertag wie im Schichtplan: eingefaerbte Spalte plus
                         Marke mit Tooltip. Der Tooltip haengt an der Marke, weil
                         der Spaltenkopf ein direktes Grid-Kind bleiben muss. */
                      marker={column.holidayName ? (
                        <AppTooltip content={column.holidayName}>
                          <span
                            className="availability__holiday-marker"
                            aria-label={t("availability.public_holiday", { name: column.holidayName })}
                          >
                            <CelebrationOutlinedIcon fontSize="inherit" />
                          </span>
                        </AppTooltip>
                      ) : null}
                    />
                  ))}
                  {showAvailabilityTotals ? (
                    <>
                      <div className="availability__totals-label">
                        <IconAvailability aria-hidden="true" />
                        <AppText as="span" variant="label-2">
                          {t("availability.availability_sum")}
                        </AppText>
                      </div>
                      {columns.map((column) => {
                        const total = availabilityTotals[column.dateKey] || 0;
                        return (
                          <div
                            key={`availability-total-${column.dateKey}`}
                            className={`availability__total-cell${total === 0 ? " is-empty" : ""}${
                              column.toneClass ? ` ${column.toneClass}` : ""
                            }`}
                            aria-label={t("availability.available_users_on_date", {
                              count: total,
                              date: column.totalsDateLabel,
                            })}
                          >
                            <AppText as="strong" variant="label-2">{total}</AppText>
                          </div>
                        );
                      })}
                    </>
                  ) : null}
                  {filteredRows.map((row) => (
                    <MatrixRow
                      key={row.user_id}
                      row={row}
                      columns={columns}
                      view={view}
                      selectedDateKeys={selectedKeysByRow.get(row.user_id) || EMPTY_SELECTION}
                      handlers={cellHandlers}
                      t={t}
                      weekdayLabels={weekdayLabels}
                    />
                  ))}
                </div>
              </div>
            </div>
          </AppCardBody>
          </AppCard>
        </div>

        {selectedCell && editorOpen ? (
          <aside className="availability__editor-pane">{editorPanelContent}</aside>
        ) : null}
      </div>

      <AppDialog
        open={editorOpen && !isDesktopEditor}
        onClose={closeEditor}
        showCloseButton={false}
        size="sm"
        className="availability__mobile-editor"
      >
        <AppDialogBody inset="none">{editorPanelContent}</AppDialogBody>
      </AppDialog>

      <AvailabilitySettingsDialog
        open={settingsOpen}
        draft={settingsDraft}
        setDraft={setSettingsDraft}
        onClose={() => setSettingsOpen(false)}
        onSave={saveSettings}
        saving={savingSettings}
        saveDisabled={!settingsHasUnsavedChanges}
      />
      <DownloadBuilder
        open={exportOpen}
        onClose={() => setExportOpen(false)}
        title={t("availability.download_title")}
        documentTitle={t("availability.title")}
        description={`${exportPreview?.period_label || currentMonth} · ${exportPreview?.scope_label || t("availability.visible_employees")}`}
        columns={exportPreview?.columns || []}
        defaultColumnIds={exportPreview?.default_column_ids || []}
        tabModes={exportPreview?.tab_modes || []}
        defaultTabMode={exportPreview?.default_tab_mode || "none"}
        timeframeOptions={[
          { id: "month", label: t("availability.month") },
          { id: "range", label: t("availability.custom") },
        ]}
        defaultTimeframe={exportTimeframe}
        onTimeframeChange={(timeframe) => { setExportTimeframe(timeframe); loadExportPreview(exportFilters, timeframe); }}
        filterGroups={exportPreview?.filter_groups || []}
        defaultFilters={exportPreview?.default_filters || exportFilters}
        resetKey={exportResetKey}
        onFiltersChange={(filters) => { setExportFilters(filters); loadExportPreview(filters, exportTimeframe); }}
        onTabModeChange={(tabMode) => {
          setExportTabMode(tabMode);
          loadExportPreview(exportFilters, exportTimeframe, tabMode);
        }}
        previewRows={exportPreview?.preview_rows || []}
        previewSections={exportPreview?.preview_sections || []}
        previewLoading={exportPreviewLoading}
        isDownloading={exporting}
        onDownload={handleExport}
        dateFieldComponent={AvailabilityDateField}
        pdfEnabled
      />
      <AppDialog
        open={pendingOpen}
        onClose={() => setPendingOpen(false)}
        size="xl"
        className="availability__pending-dialog"
      >
        <AppDialogHeader
          title={t("availability.pending.title")}
          action={selectedPendingRows.length ? (
            <AppButton
              variant="primary"
              size="sm"
              disabled={approvingSelected || Boolean(pendingDecisionId)}
              onClick={() => setPendingApproveSelectedOpen(true)}
            >
              <IconSuccess />
              {t("availability.pending.approve_selected", { count: selectedPendingRows.length })}
            </AppButton>
          ) : null}
        />
        <AppDialogBody className="availability__pending-dialog-body">
          {pendingLoading ? (
            <AppText as="p" variant="body-2" tone="tertiary" className="availability__pending-empty">
              {t("availability.loading")}
            </AppText>
          ) : (
            /*
              showStatus: Jede Zeile hier ist offen — der Dialog listet nichts
              anderes. Eine Spalte, die auf allen Zeilen "Offen" sagt, kostet
              nur die Breite, die Zeitraum und Kommentar fehlt.
            */
            <ApprovalRequestsTable
              rows={pendingRows}
              emptyState={t("availability.pending.empty")}
              decisionId={pendingDecisionId}
              paneClassName="availability__pending-table"
              density="compact"
              showStatus={false}
              selectedIds={selectedPendingIds}
              onSelectedIdsChange={setSelectedPendingIds}
              onApprove={(row) => decidePendingApproval(row, "approved")}
              onReject={(row) => {
                setPendingRejectComment("");
                setPendingRejectRow(row);
              }}
            />
          )}
        </AppDialogBody>
      </AppDialog>
      <AppConfirmDialog
        open={pendingApproveSelectedOpen}
        size="sm"
        title={t("availability.pending.approve_selected_title")}
        confirmLabel={t("button.approve")}
        confirmIcon={<IconSuccess />}
        cancelLabel={t("button.cancel")}
        onClose={() => {
          if (approvingSelected) return;
          setPendingApproveSelectedOpen(false);
        }}
        onConfirm={approveSelectedPending}
      >
        <AppText as="p" variant="body-2" tone="secondary">
          {t("availability.pending.approve_selected_question", { count: selectedPendingRows.length })}
        </AppText>
      </AppConfirmDialog>
      <AppConfirmDialog
        open={Boolean(pendingRejectRow)}
        size="sm"
        title={t("availability.pending.reject_title")}
        confirmLabel={t("button.reject")}
        confirmIcon={<IconError />}
        confirmTone="error"
        cancelLabel={t("button.cancel")}
        onClose={() => {
          if (pendingDecisionId) return;
          setPendingRejectRow(null);
          setPendingRejectComment("");
        }}
        onConfirm={() => {
          if (pendingRejectRow) {
            decidePendingApproval(pendingRejectRow, "rejected", pendingRejectComment);
          }
        }}
      >
        <div className="availability__reject-dialog-content">
          <AppText as="p" variant="body-1">
            {t("availability.pending.reject_confirm")}
          </AppText>
          <AppTextarea
            className="approval-rejection-comment"
            value={pendingRejectComment}
            onChange={(event) => setPendingRejectComment(event.target.value)}
            rows={1}
            wrap="soft"
            maxLength={1000}
            placeholder={t("availability.pending.reject_reason_placeholder")}
            aria-label={t("availability.pending.reject_reason")}
            disabled={Boolean(pendingDecisionId)}
          />
          {pendingRejectRow ? (
            <AppText variant="body-2" tone="secondary">
              {pendingRejectRow.requester_name} · {pendingRejectRow.period}
            </AppText>
          ) : null}
        </div>
      </AppConfirmDialog>
      <AppDialog
        open={Boolean(selectedDefaultsUser)}
        onClose={closeUserDefaults}
        size="sm"
        showCloseButton={!userDefaultsSaving}
      >
        <AppDialogHeader
          title={selectedDefaultsUser
            ? t("availability.user_settings.dialog_title", { name: selectedDefaultsUser.name })
            : t("availability.title")}
          subtitle={t("availability.user_settings.dialog_subtitle")}
          visual={<IconAvailability />}
        />
        <AppDialogBody>
          {/* Gleicher Aufbau wie der Einstellungs-Dialog: Feld-Stapel,
              Abschnittsueberschrift, Auswahlkarten. */}
          <AppFieldStack size="sm">
            <section>
              <AvailabilitySectionTitle
                title={t("availability.user_settings.mode_title")}
                info={t("availability.user_settings.mode_info")}
                infoLabel={t("availability.user_settings.mode_info_aria")}
              />
              <AvailabilityModeRadioGroup
                value={userDefaultsDraft.availability_mode}
                selectionCards
                onChange={(event) =>
                  setUserDefaultsDraft((current) => {
                    const nextMode = event.target.value;
                    // Der Modus setzt die Arbeitstage neu: "Abwesenheit" heisst
                    // standardmaessig an allen Oeffnungstagen verfuegbar,
                    // "Verfuegbarkeit" an keinem. Einzelne Tage und Zeiten
                    // koennen danach von Hand angepasst werden.
                    return {
                      ...current,
                      availability_mode: nextMode,
                      working_days: nextMode === "absence"
                        ? (selectedDefaultsUser?.opening_weekdays || []).map((weekday) => ({
                            weekday,
                            isFullDay: true,
                            startTime: "",
                            endTime: "",
                          }))
                        : [],
                    };
                  })
                }
              />
            </section>

            <AppDivider spacing="none" />

            <section>
              <AvailabilitySectionTitle
                title={t("availability.user_settings.working_days")}
                info={t("availability.user_settings.working_days_info")}
                infoLabel={t("availability.user_settings.working_days_info_aria")}
              />
              <WorkingDayDefaultsEditor
                value={userDefaultsDraft.working_days}
                disabled={userDefaultsSaving}
                onChange={(workingDays) =>
                  setUserDefaultsDraft((current) => ({ ...current, working_days: workingDays }))
                }
              />
            </section>
          </AppFieldStack>
        </AppDialogBody>
        <AppDialogFooter>
          <AppButton variant="neutral" disabled={userDefaultsSaving} onClick={closeUserDefaults}>
            {t("availability.user_settings.cancel")}
          </AppButton>
          <AppButton
            variant="primary"
            disabled={
              userDefaultsSaving
              || !hasAppValueChanged(initialUserDefaultsDraft, userDefaultsDraft)
              || hasInvalidWorkingDayTimes(userDefaultsDraft.working_days)
            }
            onClick={saveUserDefaults}
          >
            {t(userDefaultsSaving
              ? "availability.user_settings.saving"
              : "availability.user_settings.save")}
          </AppButton>
        </AppDialogFooter>
      </AppDialog>
    </AppWorkspace>
  );
};

export default Availability;
